import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { signOut } from 'firebase/auth';
import { collection, query, where, getDocs, doc, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { useForm } from 'react-hook-form';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './ManageNeeds.css';
import { CFR_CATEGORIES } from "../../constants/cfrCategories";


function CfrPicker({ selected, onChange }) {
  function toggle(value) {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else if (selected.length < 3) {
      onChange([...selected, value]);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem' }}>
        {CFR_CATEGORIES.map(cat => {
          const active = selected.includes(cat.value);
          const atMax = selected.length >= 3 && !active;
          return (
            <button
              key={cat.value}
              type="button"
              disabled={atMax}
              onClick={() => toggle(cat.value)}
              style={{
                padding: '0.3rem 0.8rem',
                borderRadius: '20px',
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: atMax ? 'default' : 'pointer',
                border: active ? 'none' : '1px solid #ddd',
                background: active ? '#1e3a5f' : atMax ? '#f5f5f5' : 'white',
                color: active ? 'white' : atMax ? '#bbb' : '#555',
                opacity: atMax ? 0.6 : 1,
              }}
            >
              {cat.label}
            </button>
          );
        })}
      </div>
      {selected.length >= 3 && (
        <p style={{ fontSize: '0.78rem', color: '#888', marginTop: '0.4rem' }}>
          Maximum 3 priorities selected.
        </p>
      )}
      {selected.length === 0 && (
        <p style={{ fontSize: '0.78rem', color: '#aaa', marginTop: '0.4rem' }}>
          None selected — you can add these later.
        </p>
      )}
    </div>
  );
}

function ManageNeeds() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [needs, setNeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [editingNeed, setEditingNeed] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editError, setEditError] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [discontinuingNeed, setDiscontinuingNeed] = useState(null);
  const [replacementNeedId, setReplacementNeedId] = useState('');
  const [needTypeFilter, setNeedTypeFilter] = useState('all');

  const [addCfrCategories, setAddCfrCategories] = useState([]);
  const [editCfrCategories, setEditCfrCategories] = useState([]);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => {
    if (userProfile) {
      setAddCfrCategories(userProfile.cfrCategories || []);
      fetchNeeds();
    }
  }, [userProfile]);

  async function fetchNeeds() {
    try {
      const q = query(collection(db, 'needs'), where('orgId', '==', auth.currentUser.uid));
      const snapshot = await getDocs(q);
      setNeeds(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error fetching needs:', err);
    } finally {
      setLoading(false);
    }
  }

  async function onSubmit(data) {
    try {
      setError('');
      setSubmitting(true);

      const impactQuantity = parseInt(data.impactQuantity);
      const needType = data.type || 'supply';

      await addDoc(collection(db, 'needs'), {
        orgId: auth.currentUser.uid,
        orgName: userProfile.orgName,
        type: needType,
        itemName: data.itemName,
        description: data.description,
        priority: data.priority,
        frequency: data.frequency,
        quantityPerCycle: parseInt(data.quantityPerCycle),
        pricePerCycle: null,
        priceVerifiedDate: null,
        impactQuantity,
        impactUnit: data.impactUnit,
        impactStatement: `One delivery provides ${impactQuantity} ${data.impactUnit}`,
        vendorSuggestionUrl: data.vendorSuggestionUrl || '',
        vendorName: null,
        vendorProductUrl: null,
        cfrCategories: addCfrCategories,
        status: needType === 'sponsorship' ? 'open' : 'pending_match',
        sponsorCount: 0,
        preferredDeliveryDay: parseInt(data.preferredDeliveryDay),
        community: 'springfield',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      reset();
      setShowForm(false);
      setAddCfrCategories(userProfile.cfrCategories || []);
      await fetchNeeds();
    } catch (err) {
      console.error('Error adding need:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  function startEdit(need) {
    setEditingNeed(need.id);
    setEditCfrCategories(need.cfrCategories || userProfile?.cfrCategories || []);
    setEditForm({
      type: need.type || 'supply',
      itemName: need.itemName || '',
      description: need.description || '',
      priority: need.priority || 'critical',
      frequency: need.frequency || 'monthly',
      quantityPerCycle: need.quantityPerCycle || '',
      impactQuantity: need.impactQuantity || '',
      impactUnit: need.impactUnit || '',
      preferredDeliveryDay: need.preferredDeliveryDay || 15,
      vendorSuggestionUrl: need.vendorSuggestionUrl || ''
    });
    setEditError('');
  }

  async function saveEdit(needId) {
    try {
      setEditError('');
      setEditSubmitting(true);

      const impactQuantity = parseInt(editForm.impactQuantity);

      await updateDoc(doc(db, 'needs', needId), {
        type: editForm.type || 'supply',
        itemName: editForm.itemName,
        description: editForm.description,
        priority: editForm.priority,
        frequency: editForm.frequency,
        quantityPerCycle: parseInt(editForm.quantityPerCycle),
        impactQuantity,
        impactUnit: editForm.impactUnit,
        impactStatement: `One delivery provides ${impactQuantity} ${editForm.impactUnit}`,
        vendorSuggestionUrl: editForm.vendorSuggestionUrl || '',
        cfrCategories: editCfrCategories,
        status: (editForm.type || 'supply') === 'sponsorship' ? 'open' : 'pending_review',
        pricePerCycle: null,
        preferredDeliveryDay: parseInt(editForm.preferredDeliveryDay),
        vendorName: null,
        vendorProductUrl: null,
        updatedAt: serverTimestamp(),
        editSubmittedAt: serverTimestamp()
      });

      setEditingNeed(null);
      setEditForm({});
      await fetchNeeds();

      alert(
        (editForm.type || 'supply') === 'sponsorship'
          ? 'Sponsorship updated.'
          : 'Your edits have been submitted for admin review. The need will go live again once approved.'
      );
    } catch (err) {
      console.error('Error saving edit:', err);
      setEditError('Something went wrong. Please try again.');
    } finally {
      setEditSubmitting(false);
    }
  }

  async function discontinueNeed(needId) {
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'discontinueNeed');
      await fn({ needId, replacementNeedId: replacementNeedId || null });
      setDiscontinuingNeed(null);
      setReplacementNeedId('');
      await fetchNeeds();
      alert('Need discontinued. Donors will be notified.');
    } catch (err) {
      console.error('Error discontinuing need:', err);
      alert('Error discontinuing need.');
    }
  }

  async function pauseNeed(needId, currentStatus) {
    try {
      await updateDoc(doc(db, 'needs', needId), {
        status: currentStatus === 'open' ? 'paused' : 'open',
        updatedAt: serverTimestamp()
      });
      await fetchNeeds();
    } catch (err) {
      console.error('Error updating need:', err);
    }
  }

  async function handleSignOut() {
    await signOut(auth);
    navigate('/');
  }

  const priorityColors = {
    critical: { bg: '#fee2e2', color: '#991b1b' },
    core: { bg: '#fef3c7', color: '#92400e' },
    supplemental: { bg: '#e0f2fe', color: '#075985' }
  };

  const statusColors = {
    open: { bg: '#d1fae5', color: '#065f46' },
    pending_match: { bg: '#fef3c7', color: '#92400e' },
    pending_review: { bg: '#e0f2fe', color: '#075985' },
    paused: { bg: '#f3f4f6', color: '#6b7280' },
    funded: { bg: '#dbeafe', color: '#1e40af' },
    discontinued: { bg: '#fee2e2', color: '#991b1b' }
  };

  const openNeeds = needs.filter(n => n.status === 'open' || n.status === 'funded');

  const filteredNeeds = needs.filter(n => {
    if (needTypeFilter === 'all') return true;
    return (n.type || 'supply') === needTypeFilter;
  });

  function upd(field) {
    return e => setEditForm(prev => ({ ...prev, [field]: e.target.value }));
  }

  return (
    <div className="manage-needs">

      <header className="mn-header">
        <div className="mn-logo" onClick={() => navigate('/org')}>somebody.gives</div>
        <nav className="mn-nav">
          <span onClick={() => navigate('/org/dashboard')}>Dashboard</span>
          <span className="active">Manage needs</span>
          <span onClick={() => navigate('/org/profile')}>Profile</span>
        </nav>
        <div className="mn-user">
          <span>{userProfile?.orgName}</span>
          <button onClick={handleSignOut} className="btn-signout">Sign out</button>
        </div>
      </header>

      <main className="mn-main">

        <div className="mn-top">
          <div>
            <h1>Your needs</h1>
            <p>
              List recurring supply needs or sponsorship opportunities your organization wants donors to support.
            </p>
          </div>
          <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : '+ Add a need'}
          </button>
        </div>

        {showForm && (
          <div className="need-form-card">
            <h2>Add a new need</h2>
            <p className="form-intro">
              Be specific. The more clearly you describe what you need and its impact, the more likely donors are to adopt it.
            </p>

            {error && <div className="form-error">{error}</div>}

            <form onSubmit={handleSubmit(onSubmit)} className="need-form">
              <div className="form-group">
                <label>Need type</label>
                <select {...register('type')}>
                  <option value="supply">Supply need</option>
                  <option value="sponsorship">Sponsorship opportunity</option>
                </select>
                <span className="field-hint">
                  Sponsorships connect donors directly with your organization instead of shipping products.
                </span>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Item name</label>
                  <input {...register('itemName', { required: 'Item name is required' })} placeholder="e.g. Adult socks, Diapers size 3, Table sponsor" />
                  {errors.itemName && <span className="field-error">{errors.itemName.message}</span>}
                </div>
                <div className="form-group">
                  <label>Priority level</label>
                  <select {...register('priority', { required: true })}>
                    <option value="critical">Critical — essential to daily operations</option>
                    <option value="core">Core — important recurring need</option>
                    <option value="supplemental">Supplemental — helpful but not urgent</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea {...register('description', { required: 'Description is required' })} placeholder="Describe this need and why it matters..." rows={3} />
                {errors.description && <span className="field-error">{errors.description.message}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Fulfillment frequency</label>
                  <select {...register('frequency', { required: true })}>
                    <option value="monthly">Monthly</option>
                    <option value="bi-monthly">Bi-monthly (every 2 months)</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="one-time">One-time</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Quantity per delivery / opportunity</label>
                  <input {...register('quantityPerCycle', { required: 'Quantity is required', min: { value: 1, message: 'Must be at least 1' } })} placeholder="e.g. 6" type="number" min="1" />
                  {errors.quantityPerCycle && <span className="field-error">{errors.quantityPerCycle.message}</span>}
                </div>
              </div>

              <div className="impact-section">
                <div className="impact-label">Impact statement</div>
                <p className="impact-help">
                  Complete this sentence: "One delivery provides _____ _____." For sponsorships, describe the practical impact.
                </p>
                <div className="form-row impact-row">
                  <div className="form-group">
                    <label>Number of people/items served</label>
                    <input {...register('impactQuantity', { required: 'Impact quantity is required', min: { value: 1, message: 'Must be at least 1' } })} placeholder="e.g. 48" type="number" min="1" />
                    {errors.impactQuantity && <span className="field-error">{errors.impactQuantity.message}</span>}
                  </div>
                  <div className="form-group">
                    <label>What they receive</label>
                    <input {...register('impactUnit', { required: 'Impact unit is required' })} placeholder="e.g. diaper changes, meals, children served" />
                    {errors.impactUnit && <span className="field-error">{errors.impactUnit.message}</span>}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>
                  Community Priority (CFR)
                  <span style={{ fontWeight: 400, color: '#888', marginLeft: '0.5rem', fontSize: '0.85rem' }}>— optional, up to 3</span>
                </label>
                <p style={{ fontSize: '0.82rem', color: '#666', marginBottom: '0.6rem', lineHeight: 1.5 }}>
                  Which Springfield Community Focus Report priorities does this specific need address?
                </p>
                <CfrPicker selected={addCfrCategories} onChange={setAddCfrCategories} />
              </div>

              <div className="form-group">
                <label>Do you have a specific product or reference link in mind? (optional)</label>
                <input {...register('vendorSuggestionUrl')} placeholder="Paste an Amazon, event, sponsorship, or other URL" />
                <span className="field-hint">
                  For supply needs, we will review your product suggestion. For sponsorships, this can be an event or sponsorship info link.
                </span>
              </div>

              <div className="form-group">
                <label>Preferred delivery/contact day of month</label>
                <select {...register('preferredDeliveryDay', { required: true })}>
                  {[1, 5, 10, 15, 20, 25, 28].map(d => (
                    <option key={d} value={d}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'} of each month</option>
                  ))}
                </select>
                <span className="field-hint">For supply needs, this guides delivery timing. For sponsorships, this is informational.</span>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit this need'}
                </button>
                <button type="button" className="btn-cancel" onClick={() => { setShowForm(false); reset(); }}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {loading && <div className="mn-empty">Loading your needs...</div>}

        {!loading && needs.length === 0 && !showForm && (
          <div className="mn-empty-state">
            <div className="empty-icon">📋</div>
            <h2>No needs listed yet</h2>
            <p>Add your first need. We will source the right product or list the sponsorship opportunity for donors.</p>
            <button className="btn-primary" onClick={() => setShowForm(true)}>Add your first need</button>
          </div>
        )}

        {!loading && needs.length > 0 && (
          <>
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                marginBottom: '1rem',
                flexWrap: 'wrap'
              }}
            >
              {['all', 'supply', 'sponsorship'].map(type => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setNeedTypeFilter(type)}
                  style={{
                    padding: '0.45rem 0.9rem',
                    borderRadius: '999px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 600,
                    background: needTypeFilter === type ? '#1e3a5f' : '#e5e7eb',
                    color: needTypeFilter === type ? 'white' : '#111827'
                  }}
                >
                  {type === 'all'
                    ? 'All'
                    : type === 'supply'
                      ? 'Supply Needs'
                      : 'Sponsorships'}
                </button>
              ))}
            </div>

            <div className="needs-list">
              {filteredNeeds.map(need => (
                <div key={need.id} className="need-row">

                  {editingNeed === need.id ? (
                    <div style={{ width: '100%' }}>
                      <div className="edit-form-header">
                        <h3>Edit need — changes require admin review</h3>
                        <p className="edit-form-note">
                          Supply need edits will be submitted for review. Sponsorship edits stay open unless you pause or discontinue them.
                        </p>
                      </div>
                      {editError && <div className="form-error">{editError}</div>}

                      <div className="form-group">
                        <label>Need type</label>
                        <select
                          value={editForm.type || 'supply'}
                          onChange={upd('type')}
                          className="edit-input"
                        >
                          <option value="supply">Supply need</option>
                          <option value="sponsorship">Sponsorship opportunity</option>
                        </select>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Item name</label>
                          <input value={editForm.itemName} onChange={upd('itemName')} className="edit-input" />
                        </div>
                        <div className="form-group">
                          <label>Priority</label>
                          <select value={editForm.priority} onChange={upd('priority')} className="edit-input">
                            <option value="critical">Critical</option>
                            <option value="core">Core</option>
                            <option value="supplemental">Supplemental</option>
                          </select>
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Description</label>
                        <textarea value={editForm.description} onChange={upd('description')} className="edit-input" rows={3} />
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Frequency</label>
                          <select value={editForm.frequency} onChange={upd('frequency')} className="edit-input">
                            <option value="monthly">Monthly</option>
                            <option value="bi-monthly">Bi-monthly</option>
                            <option value="quarterly">Quarterly</option>
                            <option value="one-time">One-time</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Quantity per delivery / opportunity</label>
                          <input type="number" value={editForm.quantityPerCycle} onChange={upd('quantityPerCycle')} className="edit-input" />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>People/items served per delivery</label>
                          <input type="number" value={editForm.impactQuantity} onChange={upd('impactQuantity')} className="edit-input" />
                        </div>
                        <div className="form-group">
                          <label>What they receive</label>
                          <input value={editForm.impactUnit} onChange={upd('impactUnit')} className="edit-input" />
                        </div>
                      </div>

                      {editForm.impactQuantity && editForm.impactUnit && (
                        <p className="impact-preview">Preview: "One delivery provides {editForm.impactQuantity} {editForm.impactUnit}"</p>
                      )}

                      <div className="form-group">
                        <label>
                          Community Priority (CFR)
                          <span style={{ fontWeight: 400, color: '#888', marginLeft: '0.5rem', fontSize: '0.85rem' }}>— optional, up to 3</span>
                        </label>
                        <p style={{ fontSize: '0.82rem', color: '#666', marginBottom: '0.6rem', lineHeight: 1.5 }}>
                          Which Springfield Community Focus Report priorities does this need address?
                        </p>
                        <CfrPicker selected={editCfrCategories} onChange={setEditCfrCategories} />
                      </div>

                      <div className="form-group">
                        <label>Vendor/reference URL (optional)</label>
                        <input value={editForm.vendorSuggestionUrl} onChange={upd('vendorSuggestionUrl')} className="edit-input" placeholder="https://amazon.com/..." />
                      </div>

                      <div className="form-group">
                        <label>Preferred delivery/contact day of month</label>
                        <select value={editForm.preferredDeliveryDay || 15} onChange={upd('preferredDeliveryDay')} className="edit-input">
                          {[1, 5, 10, 15, 20, 25, 28].map(d => (
                            <option key={d} value={d}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'} of each month</option>
                          ))}
                        </select>
                        <span className="field-hint">For supply needs, this guides delivery timing. For sponsorships, this is informational.</span>
                      </div>

                      <div className="edit-form-actions">
                        <button className="btn-primary" onClick={() => saveEdit(need.id)} disabled={editSubmitting}>
                          {editSubmitting ? 'Submitting...' : 'Submit edits'}
                        </button>
                        <button className="btn-cancel" onClick={() => { setEditingNeed(null); setEditForm({}); }}>Cancel</button>
                      </div>
                    </div>

                  ) : discontinuingNeed === need.id ? (
                    <div style={{ width: '100%' }}>
                      <div className="discontinue-header">
                        <h3>Discontinue: {need.itemName}</h3>
                        <p className="discontinue-note">
                          This need has {need.sponsorCount || 0} active sponsor{need.sponsorCount !== 1 ? 's' : ''}. They will be notified and their commitment will be cancelled. If you designate a replacement need below, they will be offered the option to adopt it instead.
                        </p>
                      </div>
                      <div className="form-group">
                        <label>Replacement need (optional but recommended)</label>
                        <select value={replacementNeedId} onChange={e => setReplacementNeedId(e.target.value)} className="edit-input">
                          <option value="">No replacement — cancel donor commitments</option>
                          {openNeeds.filter(n => n.id !== need.id).map(n => (
                            <option key={n.id} value={n.id}>
                              {n.itemName} (${n.pricePerCycle ? Math.ceil(n.pricePerCycle) : 'TBD'}/{n.frequency})
                            </option>
                          ))}
                        </select>
                        <span className="field-hint">Donors will be offered this need as a replacement for their cancelled commitment.</span>
                      </div>
                      <div className="edit-form-actions">
                        <button className="btn-discontinue" onClick={() => discontinueNeed(need.id)}>Confirm discontinue</button>
                        <button className="btn-cancel" onClick={() => { setDiscontinuingNeed(null); setReplacementNeedId(''); }}>Cancel</button>
                      </div>
                    </div>

                  ) : (
                    <>
                      <div className="need-row-left">
                        <div className="need-row-top">
                          <span
                            className="status-pill"
                            style={{
                              background: (need.type || 'supply') === 'sponsorship' ? '#f3e8ff' : '#e0f2fe',
                              color: (need.type || 'supply') === 'sponsorship' ? '#6b21a8' : '#075985'
                            }}
                          >
                            {need.type || 'supply'}
                          </span>

                          <span className="priority-pill" style={{ background: priorityColors[need.priority]?.bg, color: priorityColors[need.priority]?.color }}>
                            {need.priority}
                          </span>
                          <span className="status-pill" style={{ background: statusColors[need.status]?.bg, color: statusColors[need.status]?.color }}>
                            {need.isChild && <span style={{ fontSize: '0.75rem', color: '#888', fontStyle: 'italic' }}>Unit {need.unitNumber} of {need.totalUnits}</span>}
                            {need.status === 'pending_match' ? 'pending review' : need.status === 'pending_review' ? 'edit pending review' : need.status}
                          </span>
                          <span className="need-frequency">{need.frequency}</span>
                        </div>
                        <div className="need-row-name">{need.itemName}</div>
                        <div className="need-row-impact">{need.impactStatement}</div>

                        {(need.cfrCategories || []).length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.4rem' }}>
                            {(need.cfrCategories || []).map(val => {
                              const cat = CFR_CATEGORIES.find(c => c.value === val);
                              return cat ? (
                                <span key={val} style={{ padding: '0.15rem 0.6rem', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 600, background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd' }}>
                                  {cat.label}
                                </span>
                              ) : null;
                            })}
                          </div>
                        )}

                        {need.status === 'pending_match' && <div className="need-row-pending-note">We are researching the best product for this need. It will go live once we confirm sourcing.</div>}
                        {need.isChild && <div className="need-row-pending-note">This need was split into {need.totalUnits} units by somebody.gives to make it easier for multiple donors to cover together. Each unit represents 1/{need.totalUnits} of the original need.</div>}
                        {need.status === 'pending_review' && <div className="need-row-pending-note">Your edits are under review. The need will go live again once approved.</div>}
                      </div>

                      <div className="need-row-right">

                        <div
                          style={{
                            fontSize: '0.85rem',
                            fontWeight: 700,
                            color: '#555',
                            marginBottom: '0.35rem'
                          }}
                        >
                          {need.frequency}
                        </div>

                        <div className="need-row-sponsors">
                          {(need.type || 'supply') === 'sponsorship'
                            ? `${need.sponsorshipInterestCount || 0} interested donor${need.sponsorshipInterestCount !== 1 ? 's' : ''}`
                            : `${need.sponsorCount || 0} sponsor${need.sponsorCount !== 1 ? 's' : ''}`}
                        </div>

                        {need.status === 'pending_match' && <div className="need-row-actions"><span className="need-action-note">Awaiting admin review</span></div>}
                        {need.status === 'pending_review' && <div className="need-row-actions"><span className="need-action-note">Edit under review</span></div>}

                        {(need.status === 'open' || need.status === 'paused') && (
                          <div className="need-row-actions">
                            {(need.sponsorCount || 0) === 0 ? (
                              <button className="btn-edit-need" onClick={() => startEdit(need)}>Edit</button>
                            ) : (
                              <span className="need-action-note">Sponsored — cannot edit</span>
                            )}
                            <button className="btn-pause" onClick={() => pauseNeed(need.id, need.status)}>
                              {need.status === 'open' ? 'Pause' : 'Resume'}
                            </button>
                            <button className="btn-discontinue-sm" onClick={() => { setDiscontinuingNeed(need.id); setReplacementNeedId(''); }}>Discontinue</button>
                          </div>
                        )}

                        {need.status === 'funded' && (
                          <div className="need-row-actions">
                            <span className="need-action-note">Fully sponsored — cannot edit</span>
                            <button className="btn-discontinue-sm" onClick={() => { setDiscontinuingNeed(need.id); setReplacementNeedId(''); }}>Discontinue</button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

      </main>
    </div>
  );
}

export default ManageNeeds;